/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useLocation } from "react-router";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { useForm } from "@/src/provider/Form/FormProvider";
import { getCaseLink } from "@/src/services/productService";
import ProductBox from "@/src/components/ProductBox/ProductBox";
import InsuranceData from "@/src/components/Form/Settlement/Summary/InsuranceData";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import EmailVerificationModal from "@/src/components/FormInfoModal/EmailVerificationModal";
import summaryStyle from "@/src/components/Form/ContractLaw/Summary/summaryStyle";
import SummaryDetailsItem from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsItem";
import SummaryDetailsPersonItems from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsPersonItems";
import SummaryDetailsCustomValueItem from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsCustomValueItem";

type SummaryProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

const Summary = ({ product, submitProps }: SummaryProps) => {
  const location = useLocation();
  const stateField = location.state?.field;
  const { handleSubmit } = useForm();

  useEffect(() => {
    const el = document.getElementById(stateField);
    el && el.scrollIntoView();
  }, [stateField]);

  return (
    <ProductBox>
      <Typography variant={"h5"}>
        Ihre Angaben
      </Typography>
      <Grid container>
        <Box id={"generalCivilLawBase"} sx={summaryStyle.scrollToAnchor} />
        <Grid sx={summaryStyle.subHeadline} size={12}>
          <Typography variant={"h6"}>Ihr Anliegen</Typography>
        </Grid>
        <SummaryDetailsPersonItems basePath={"customer.person"} caseData={product} />

        <SummaryDetailsItem fieldPath={"customer.telephone"} caseData={product} />

        <SummaryDetailsItem fieldPath={"civilServiceStatus"} caseData={product} />

        {product.caseCategory !== "advisory" && product.clientIsCustomer !== true && !product.clientWasMinorAtContractDate && (
          <>
            <SummaryDetailsPersonItems basePath={"client"} caseData={product} />
          </>
        )}
        {product.caseCategory !== "advisory" && product.clientIsCustomer !== true && product.clientWasMinorAtContractDate && (
          <>
            <Grid sx={{ marginTop: "2rem", borderBottom: "1px solid #333", "& h6": { lineHeight: "1rem" } }} size={12}>
              <Typography variant={"h6"}>Angaben minderjährige Person</Typography>
            </Grid>

            <SummaryDetailsPersonItems basePath={"client"} caseData={product} />

            <SummaryDetailsPersonItems basePath={"clientLegalRepresentative"} caseData={product} />

            {product.clientLegalRepresentativeAmount === 2 && (
              <>
                <SummaryDetailsPersonItems basePath={"secondClientLegalRepresentative"} caseData={product} />
                <SummaryDetailsItem fieldPath={"clientLegalRepresentativesAuthorizationType"} caseData={product} />
              </>
            )}
          </>
        )}

        {product.caseCategory !== "advisory" && (
          <>
            <Box id={"civilServiceLawEmploymentRelationship"} sx={summaryStyle.scrollToAnchor} />
            <Grid sx={summaryStyle.subHeadline} size={12}>
              <Typography variant={"h6"}>Informationen zur Gegenseite</Typography>
            </Grid>
            <SummaryDetailsItem fieldPath={"employerType"} caseData={product} />
            {product.employerType === "state" && <SummaryDetailsItem fieldPath={"employerState"} caseData={product} />}
            {product.employerType === "municipal" && <SummaryDetailsItem fieldPath={"employerMunicipal"} caseData={product} />}
            <SummaryDetailsItem fieldPath={"authorityName"} caseData={product} />
            <SummaryDetailsItem fieldPath={"workLocationPostalCode"} caseData={product} />

            <SummaryDetailsItem fieldPath={"personnelNumber"} caseData={product} />
            <SummaryDetailsCustomValueItem
              fieldPath={"authorityAddress"}
              caseData={product}
              value={`${product.authorityAddress.streetAddress}<br>${product.authorityAddress.postalCode} ${product.authorityAddress.addressLocality}`}
            />
            <SummaryDetailsItem fieldPath={"authorityTelephone"} caseData={product} />
          </>
        )}

        <Box id={"concernSummary"} sx={summaryStyle.scrollToAnchor} />
        <Grid sx={summaryStyle.subHeadline} size={12}>
          <Typography variant={"h6"}>Zusammenfassung Ihres Anliegens</Typography>
        </Grid>
        <SummaryDetailsItem fieldPath={"finalClientDescriptionAndObjectives"} caseData={product} />

        <>
          <Box id={"insuranceData"} sx={summaryStyle.scrollToAnchor} />
          <InsuranceData title={"Versicherung"} product={product} />
        </>
      </Grid>
      <form onSubmit={handleSubmit}>
        <FormSubmit
          sx={{ marginTop: "1rem" }}
          {...submitProps}
          hideBackButton={false}
          backLink={getCaseLink(product) + "/antrag/anliegen"}
          backText={"Alles bearbeiten"}
          nextText={"Angaben speichern"}
        />
      </form>
      <EmailVerificationModal />
    </ProductBox>
  );
};

export default Summary;
